/**
 * /wireline-slickline/units — the tenant's wireline and slickline units
 * (GET /wireline-slickline/units, fieldiq-api#1336, WL-1).
 *
 * Full-width register; a row opens the unit on its own page, where its crew
 * templates live. A unit is a spec on a unit in Equipment, so the equipment's
 * name sits under the unit number. Ratings show as entered; absent is `—`.
 *
 * The counts in the band are a tally of the rows this list returned (no
 * endpoint returns one); a failed read shows `—`, never 0.
 *
 * New Unit is WL_ADMIN (admin, office_staff) — lib/wireline-slickline.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Truck, Users, UserX } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useWlUnits } from '../../hooks/useWirelineSlickline';
import { queryLoadFailure } from '../../lib/load-failure';
import { canAdminWl, wlWithUnit } from '../../lib/wireline-slickline';
import type { WlUnit } from '../../types/wireline-slickline';
import { useOpenRecord } from '../downhole/recordPage';
import { WL_EYEBROW, WL_RAIL, WlEmpty, WlPanel, WlRegister, type WlColumn } from './WlBits';

const lineTypesText = (u: WlUnit) => (u.lineTypeLabels?.length ? u.lineTypeLabels : u.lineTypes ?? []).join(', ') || '—';

export default function WlUnitsScreen() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const query = useWlUnits();
  const units = query.data?.data ?? [];
  const loading = query.isLoading;
  const known = loading || queryLoadFailure(query) ? undefined : units;
  const canAdd = canAdminWl(user?.role);

  const columns: WlColumn<WlUnit>[] = [
    {
      key: 'unit',
      header: 'Unit',
      render: (u) => (
        <>
          <p className="font-extrabold text-white">{u.unitNumber || '—'}</p>
          <p className="mt-0.5 text-xs text-fieldiq-muted">
            {u.equipmentName || '—'}
            {u.equipmentUnitNumber ? ` · ${u.equipmentUnitNumber}` : ''}
          </p>
        </>
      ),
    },
    { key: 'lines', header: 'Line types', render: lineTypesText },
    { key: 'drums', header: 'Drums', align: 'right', render: (u) => <span className="font-mono text-sm">{displayValue(u.drumCount)}</span> },
    { key: 'mast', header: 'Mast', render: (u) => u.mastKindLabel ?? u.mastKind ?? '—' },
    { key: 'rating', header: 'Mast rating', render: (u) => <span className="font-mono text-sm">{wlWithUnit(u.mastRating, u.mastRatingUnit)}</span> },
    { key: 'depth', header: 'Max depth', render: (u) => <span className="font-mono text-sm">{wlWithUnit(u.maxDepthRating, u.maxDepthRatingUnit)}</span> },
    { key: 'crews', header: 'Crews', align: 'right', render: (u) => <span className="font-mono text-sm">{displayValue(u.crewCount)}</span> },
  ];

  return (
    <div data-testid="wl-units-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WL_EYEBROW}
        title="Units"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canAdd ? (
            <button type="button" onClick={() => openRecord('/wireline-slickline/units/new')} className="fi-action fi-action--standard fi-action--primary">
              <Plus className="size-4" aria-hidden="true" />
              New unit
            </button>
          ) : undefined
        }
        stats={[
          { icon: <Truck className="size-4" aria-hidden="true" />, label: 'Units', sub: 'Registered', tone: 'neutral', loading, value: displayValue(known?.length) },
          {
            icon: <Users className="size-4" aria-hidden="true" />,
            label: 'With a crew',
            tone: 'neutral',
            loading,
            value: displayValue(known ? known.filter((u) => u.crewCount > 0).length : undefined),
          },
          {
            icon: <UserX className="size-4" aria-hidden="true" />,
            label: 'No crew',
            tone: 'accent',
            loading,
            value: displayValue(known ? known.filter((u) => !u.crewCount).length : undefined),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <WlPanel
            eyebrow="Unit register"
            title="Units"
            testId="wl-unit-register"
            footer={!loading && known && units.length > 0 ? `${units.length} unit${units.length === 1 ? '' : 's'}` : undefined}
          >
            <WlRegister
              query={query}
              rows={units}
              columns={columns}
              rail={() => WL_RAIL}
              onOpen={(u) => openRecord(`/wireline-slickline/units/${u.id}`)}
              subject="units"
              testId="wl-unit"
              minWidth="min-w-[860px]"
              empty={<WlEmpty>There are no wireline units yet.{canAdd ? ' Add one from a unit in Equipment.' : ''}</WlEmpty>}
              card={(u) => (
                <>
                  <p className="text-sm font-extrabold text-white">{u.unitNumber || '—'}</p>
                  <p className="mt-0.5 text-xs text-fieldiq-muted">
                    {u.equipmentName || '—'} · {lineTypesText(u)}
                  </p>
                  <p className="mt-2 text-xs text-fieldiq-text">
                    {wlWithUnit(u.mastRating, u.mastRatingUnit)} · {displayValue(u.crewCount)} crew{u.crewCount === 1 ? '' : 's'}
                  </p>
                </>
              )}
            />
          </WlPanel>
        </div>
      </section>
    </div>
  );
}
